import React, { useContext, useState, useRef } from "react";
import Ctx from "../../contexts/AppContext";
import { api } from "../../api";
import { crd, bdg, btnP, btnSec, sp, rad } from "../../constants/styles";
import Ic from "../common/Ic";
import { getLeaseStatus, getDaysRemaining, isExpiringSoon, leaseStatusColor, leaseStatusLabel, getLeaseForTenant } from "../../utils/leaseHelpers";
import { defaultTerms, generateAgreementHTML, buildAgreementDataFromLease, buildSigHTML } from "../../utils/agreement";

function TenLeasePage() {
  const { t, h, leases, setLeases, tens, setTens, props, currUserId, go, flash } = useContext(Ctx);
  const me = tens.find(x => x.id === currUserId);
  const lease = getLeaseForTenant(leases, currUserId);
  const myProp = props.find(x => x.id === (lease?.property_id || me?.pid));
  const status = getLeaseStatus(lease);
  const days = getDaysRemaining(lease);
  const expSoon = isExpiringSoon(lease);
  const [showSign, setShowSign] = useState(false);
  const [signing, setSigning] = useState(false);
  const [showClauses, setShowClauses] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Parse terms from lease
  const parseTerms = () => { try { return typeof lease?.terms === 'string' ? JSON.parse(lease.terms) : (lease?.terms || {}); } catch { return {}; } };
  const terms = { ...defaultTerms, ...parseTerms() };

  // Signature pad handlers
  const startDraw = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!isDrawing) return; e.preventDefault();
    const canvas = canvasRef.current; const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.strokeStyle = h.tx; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.lineTo(x, y); ctx.stroke();
  };
  const endDraw = () => setIsDrawing(false);
  const clearSign = () => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); };

  const submitSign = async () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const blank = document.createElement("canvas"); blank.width = canvas.width; blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) { flash("Please draw your signature first", "error"); return; }
    setSigning(true);
    try {
      const updatedMe = { ...me, esign: dataUrl };
      setTens(tens.map(x => x.id === me.id ? updatedMe : x));
      await api.saveTenant(updatedMe);
      if (lease) {
        const updatedLease = { ...lease, status: "active", signed_at: new Date().toISOString() };
        setLeases(leases.map(l => l.id === lease.id ? updatedLease : l));
        await api.saveLease(updatedLease);
      }
      setShowSign(false);
      flash("Agreement signed successfully! \u2705");
    } catch (e) { flash("Failed to save signature", "error"); }
    setSigning(false);
  };

  // PDF
  const printPDF = () => {
    if (!lease) return;
    const data = buildAgreementDataFromLease(lease, me, myProp);
    if (!data) { flash("Missing data", "error"); return; }
    const html = generateAgreementHTML(data);
    // SECURITY: noopener,noreferrer prevents reverse tabnabbing
    const w = window.open("", "_blank", "noopener,noreferrer");
    if (!w) { flash("Allow popups to view agreement", "error"); return; }
    w.document.write(html); w.document.close();
  };

  // No lease
  if (!lease) return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>My Agreement</h2>
    <div style={{ ...crd(h), textAlign: "center", padding: 24 }}>
      <Ic n="file" s={32} c={h.sc} />
      <div style={{ color: h.sc, fontSize: 14, marginTop: 10 }}>No agreement yet</div>
      {me?.pid && myProp && <div style={{ marginTop: 14, borderTop: `1px solid ${h.bd}`, paddingTop: 14 }}>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{myProp.addr}</div>
        <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{myProp.rooms} rooms \u00B7 \u20B9{myProp.rent?.toLocaleString()}/mo</div>
      </div>}
    </div>
  </div>;

  const row = (label, val) => <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${h.bd}30` }}>
    <span style={{ color: h.sc, fontSize: 12 }}>{label}</span>
    <span style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{val}</span>
  </div>;

  const isDraft = status === "draft";
  const isSigned = !!lease.signed_at;
  const llSig = terms.landlord_signature;

  return <div style={{ paddingTop: 16 }}>
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 8 }}>&larr; {t.back}</button>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>My Agreement</h2>
      <span style={bdg(`${leaseStatusColor(status, h)}20`, leaseStatusColor(status, h))}>{leaseStatusLabel(status, t)}</span>
    </div>

    {/* Draft Banner */}
    {isDraft && !showSign && <div style={{ background: `${h.ac}10`, borderRadius: 12, padding: 14, marginBottom: 12, border: `1px solid ${h.ac}30` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 16 }}>{"\uD83D\uDCCB"}</span>
        <span style={{ color: h.ac, fontWeight: 700, fontSize: 13 }}>Agreement Ready for Review</span>
      </div>
      <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5, marginBottom: 10 }}>Your landlord has created an agreement. Review the terms below and sign to activate.</div>
      <button onClick={() => setShowSign(true)} style={{ ...btnP(h), width: "100%", padding: "10px 16px" }}>{"\u270D\uFE0F"} Review & Sign</button>
    </div>}

    {/* Signed confirmation */}
    {isSigned && <div style={{ background: `${h.gn}10`, borderRadius: 12, padding: 14, marginBottom: 12, border: `1px solid ${h.gn}30` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>{"\u2705"}</span>
        <span style={{ color: h.gn, fontWeight: 700, fontSize: 13 }}>Agreement Signed & Active</span>
      </div>
      <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>Signed on {new Date(lease.signed_at).toLocaleDateString("en-IN")}</div>
    </div>}

    {expSoon && <div style={{ background: `${h.wn}15`, borderRadius: 10, padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 14 }}>{"\u26A0\uFE0F"}</span>
      <span style={{ color: h.wn, fontSize: 12, fontWeight: 600 }}>{days} days remaining</span>
    </div>}

    {/* Property Info */}
    <div style={crd(h)}>
      <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Property</div>
      <div style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{myProp?.addr || "\u2014"}</div>
      {myProp?.rooms && <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{myProp.rooms} rooms</div>}
    </div>

    {/* Lease Terms */}
    <div style={crd(h)}>
      <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Lease Terms</div>
      {row("Start Date", lease.start_date || "\u2014")}
      {row("End Date", lease.end_date || "\u2014")}
      {row("Days Remaining", days != null ? days : "\u2014")}
      {row("Rent", `\u20B9${Number(lease.rent || 0).toLocaleString()}/mo`)}
      {row("Deposit", `\u20B9${Number(lease.deposit || 0).toLocaleString()}`)}
      {row("Notice Period", `${lease.notice_period_days || 30} days`)}
      {lease.lock_in_months && row("Lock-in", `${lease.lock_in_months} months`)}
      {lease.escalation_pct && row("Escalation", `${lease.escalation_pct}% annually`)}
    </div>

    {/* Agreement Clauses (collapsible) */}
    <div style={crd(h)}>
      <button onClick={() => setShowClauses(!showClauses)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, padding: 0 }}>
        {showClauses ? "\u25BE" : "\u25B8"} Agreement Clauses & Terms
      </button>
      {showClauses && <div style={{ marginTop: 10 }}>
        {terms.maintenance_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Maintenance</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.maintenance_clause}</div></div>}
        {terms.usage_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Usage</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.usage_clause}</div></div>}
        {terms.electricity_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Electricity/Water</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.electricity_clause}</div></div>}
        {terms.fittings && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Fittings</div><div style={{ color: h.sc, fontSize: 11 }}>{terms.fittings}</div></div>}
        {terms.property_tax_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Property Tax</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.property_tax_clause}</div></div>}
        {terms.painting_clause && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Painting/Repair</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.painting_clause}</div></div>}
        {terms.special_terms && <div style={{ marginBottom: 8 }}><div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>Special Terms</div><div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{terms.special_terms}</div></div>}
        {/* Custom clauses */}
        {(terms.custom_clauses || []).map((cc, i) => cc.title || cc.text ? <div key={`cc_${i}`} style={{ marginBottom: 8 }}>
          <div style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>{cc.title || `Additional Clause ${i + 1}`}</div>
          <div style={{ color: h.sc, fontSize: 11, lineHeight: 1.5 }}>{cc.text}</div>
        </div> : null)}
      </div>}
    </div>

    {/* Signatures */}
    <div style={crd(h)}>
      <div style={{ color: h.ac, fontWeight: 600, fontSize: 12, marginBottom: 8 }}>Signatures</div>
      {llSig ? <div style={{ background: `${h.gn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.gn}25`, marginBottom: 8 }}>
        <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"\u2713"} Landlord Signed</div>
        {llSig.startsWith("sig|") && <img src={llSig.split("|").slice(2).join("|")} alt="sig" style={{ maxHeight: 50 }} />}
      </div> : <div style={{ color: h.wn, fontSize: 11, marginBottom: 8 }}>{"\u23F3"} Awaiting landlord signature</div>}

      {me?.esign ? <div>
        <div style={{ background: `${h.gn}10`, padding: 10, borderRadius: 8, border: `1px solid ${h.gn}25` }}>
          <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{"\u2713"} You Signed</div>
          <img src={me.esign.startsWith("sig|") ? me.esign.split("|").slice(2).join("|") : me.esign} alt="sig" style={{ maxHeight: 50 }} />
          {me.esign.startsWith("sig|") && <div style={{ color: h.sc, fontSize: 9, marginTop: 2 }}>Date: {me.esign.split("|")[1]}</div>}
        </div>
        <button onClick={() => setShowSign(true)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600, padding: 0, marginTop: 4 }}>{"\u270F\uFE0F"} Re-Sign</button>
      </div> : (!showSign && <button onClick={() => setShowSign(true)} style={{ ...btnP(h), width: "100%", padding: 10 }}>{"\u270D\uFE0F"} Sign Agreement</button>)}
    </div>

    {/* E-Signature Pad */}
    {showSign && <div style={{ ...crd(h), border: `2px solid ${h.ac}40`, padding: 16 }}>
      <div style={{ color: h.ac, fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{"\u270D\uFE0F"} E-Sign Agreement</div>
      <div style={{ color: h.sc, fontSize: 11, marginBottom: 8, lineHeight: 1.5 }}>By signing below, I agree to the terms shown above. This e-signature is legally binding under the Indian IT Act, 2000.</div>
      <div style={{ border: `1px solid ${h.bd}`, borderRadius: 10, marginBottom: 8, background: h.bg, overflow: "hidden" }}>
        <canvas ref={canvasRef} width={360} height={120} style={{ width: "100%", height: 120, touchAction: "none", cursor: "crosshair" }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={clearSign} style={{ ...btnSec(h), flex: 1 }}>Clear</button>
        <button onClick={() => setShowSign(false)} style={{ ...btnSec(h), flex: 1 }}>Cancel</button>
        <button onClick={submitSign} disabled={signing} style={{ ...btnP(h), flex: 2, opacity: signing ? 0.6 : 1 }}>{signing ? "Saving..." : "\u2705 Sign & Activate"}</button>
      </div>
    </div>}

    {/* Download PDF */}
    <button onClick={printPDF} style={{ ...btnP(h), marginTop: 4, marginBottom: 24, width: "100%" }}>{"\uD83D\uDCC4"} Download Agreement PDF</button>
  </div>;
}

export default TenLeasePage;
